// Landing page sections. Each is a self-contained band; LandingPage.tsx
// composes them between the hero and the footer.
import React, { useState } from 'react'
import {
  Layers, Zap, Check, X, ArrowRight, Calendar, FileText, Inbox as InboxIcon,
  MessageSquare, Video, Truck, ShieldCheck, BarChart3, Bot, Users, Building2,
  Wrench, HardHat, Plus, ClipboardList, Repeat, Radio,
} from 'lucide-react'
import { Reveal, SectionHead, Button, Avatar } from './primitives'
import {
  DispatchBoard, InboxPanel, InvoicePanel, ChatPanel, FleetPanel,
  AnalyticsPanel, AutomationPanel,
} from './panels'
import { REPLACES, TEAM } from './fixtures'

// ═══ Consolidation marquee ═════════════════════════════════════════════════
export function ReplacesSection() {
  const loop = [...REPLACES, ...REPLACES]
  return (
    <section className="ork-section ork-section--flush" style={{ paddingBlock: 'clamp(40px,6vw,72px)' }} aria-label="Tools Orkestra replaces">
      <div className="ork-container" style={{ textAlign: 'center', marginBottom: 26 }}>
        <p className="ork-kicker" style={{ fontWeight: 600 }}>One login instead of ten. Orkestra folds in the whole stack:</p>
      </div>
      <div className="ork-marquee">
        <div className="ork-marquee__track">
          {loop.map((t, i) => (
            <span key={i} className="ork-tool"><Check size={15} style={{ color: 'var(--positive)' }} />{t}</span>
          ))}
        </div>
      </div>
    </section>
  )
}

// ═══ Problem ═══════════════════════════════════════════════════════════════
export function ProblemSection() {
  const pains = [
    { t: 'The job lives in five places', d: 'A request in the inbox, the schedule in one app, the quote in another, the invoice in a third, and the details in someone’s head.' },
    { t: 'Nothing reconciles', d: 'Re-typing the same customer into every tool. Numbers that never match. Reports you rebuild by hand every month.' },
    { t: 'Handoffs fall through', d: 'Dispatch texts the tech, the tech calls the office, the customer emails and hears nothing back. Work slips between the seams.' },
  ]
  return (
    <section className="ork-section">
      <div className="ork-container">
        <SectionHead
          eyebrow="The real problem"
          title={<>Your business runs on <span className="ork-gradient-text">duct tape</span>.</>}
          lead="Most service teams stitch together a CRM, a scheduler, a billing tool, an inbox, and a chat app. The software doesn’t talk — so your people do all the reconciling."
        />
        <div className="ork-grid ork-grid--3">
          {pains.map((p, i) => (
            <Reveal key={p.t} delay={i * 90}>
              <div className="ork-card" style={{ height: '100%' }}>
                <div className="ork-card__icon" style={{ background: 'var(--danger-dim)', color: 'var(--danger)' }}><X size={20} /></div>
                <h3 className="ork-h3" style={{ fontSize: 18 }}>{p.t}</h3>
                <p className="ork-body" style={{ marginTop: 8, fontSize: 15 }}>{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

// ═══ Unified / connected workflow ══════════════════════════════════════════
export function UnifiedSection() {
  const steps = [
    { icon: <InboxIcon size={22} />, label: 'Request', sub: 'Email, portal or phone' },
    { icon: <Calendar size={22} />, label: 'Dispatch', sub: 'Right tech, right time' },
    { icon: <HardHat size={22} />, label: 'On site', sub: 'Status, photos, notes' },
    { icon: <FileText size={22} />, label: 'Invoice', sub: 'With margin captured' },
    { icon: <BarChart3 size={22} />, label: 'Insight', sub: 'Analytics update live' },
  ]
  return (
    <section className="ork-section">
      <div className="ork-container">
        <SectionHead
          eyebrow="One connected system"
          title={<>Every step of the job, on <span className="ork-gradient-text">one thread</span>.</>}
          lead="A request becomes a job, a job becomes an invoice, and every number rolls straight into analytics — no re-entry, no reconciling, no gaps between departments."
        />
        <Reveal>
          <div className="ork-flow">
            <div className="ork-flow__line" />
            <div className="ork-flow__pulse" />
            {steps.map((s, i) => (
              <div className="ork-flow__step" key={s.label}>
                <div className="ork-flow__node">{s.icon}</div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--ink-primary)' }}>{s.label}</div>
                  <div style={{ fontSize: 13, color: 'var(--ink-tertiary)' }}>{s.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal className="ork-split__media" delay={120}>
          <div style={{ maxWidth: 860, margin: '46px auto 0' }}>
            <WorkflowFilm />
          </div>
        </Reveal>
      </div>
    </section>
  )
}

// Ambient product film — muted, looping, lazy. Honors reduced-motion by
// showing the poster still instead of autoplaying. The clip is generated
// locally (scripts/landing-film.mjs) from the /preview-film route.
function WorkflowFilm() {
  const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  return (
    <div className="ork-frame">
      <div className="ork-frame__bar"><span className="ork-frame__dot" /><span className="ork-frame__dot" /><span className="ork-frame__dot" /><span className="ork-frame__url">orkestra.app · the workflow, live</span></div>
      <div className="ork-film">
        {reduced ? (
          <img className="ork-film__video" src="/landing/orkestra-workflow-poster.png" alt="Orkestra turns a customer request into a scheduled job, an invoice with captured margin, and live analytics — all on one thread." />
        ) : (
          <video
            className="ork-film__video"
            src="/landing/orkestra-workflow.webm"
            poster="/landing/orkestra-workflow-poster.png"
            autoPlay muted loop playsInline preload="none"
            aria-label="Orkestra turns a customer request into a scheduled job, an invoice with captured margin, and live analytics — all on one thread."
          />
        )}
        <span className="ork-film__badge"><span className="ork-dot" style={{ background: 'var(--positive)' }} /> Request → dispatch → invoice → insight</span>
      </div>
    </div>
  )
}

// ═══ Interactive product tour (tabs) ═══════════════════════════════════════
// Most tabs render a built dark panel inside a browser frame. The Meetings tab
// renders a self-chromed image (its own window chrome) — so it is NOT wrapped
// in an extra frame — and carries an in-context "simulated participants"
// disclosure (the participant faces are AI-generated; owner decision, see
// LANDING_PROJECT.md decision log).
interface TourTab { id: string; label: string; icon: React.ReactNode; url: string; title: string; body: string; panel?: React.ReactNode; image?: string; disclosure?: string }
const TOUR: TourTab[] = [
  { id: 'dispatch', label: 'Dispatch', icon: <Calendar size={15} />, panel: <DispatchBoard />, url: 'app/field',
    title: 'A schedule your dispatcher actually trusts', body: 'See every tech, every job, and every hold on one board. Orkestra flags scheduling conflicts before they become a missed appointment, and status flows from the field in real time.' },
  { id: 'billing', label: 'Quotes & invoices', icon: <FileText size={15} />, panel: <InvoicePanel />, url: 'app/invoices',
    title: 'Quote, invoice, and see your margin', body: 'Turn an approved quote into an invoice in a click. Cost snapshots from the field mean you see the real margin on every job — not a guess at month-end.' },
  { id: 'inbox', label: 'Shared inbox', icon: <InboxIcon size={15} />, panel: <InboxPanel />, url: 'app/inbox',
    title: 'Customer email that isn’t a free-for-all', body: 'Email, phone, and portal messages land in one shared inbox with assignment, priority, and status. A first-response SLA clock flags any conversation about to breach — so nothing sits unanswered and everyone can see who’s got it.' },
  { id: 'chat', label: 'Team chat', icon: <MessageSquare size={15} />, panel: <ChatPanel />, url: 'app/team-chat',
    title: 'Your team’s back-channel, built in', body: 'Channels, DMs, threads, mentions, and reactions — the coordination that used to live in Teams or a group text now sits right next to the work it’s about.' },
  { id: 'meet', label: 'Meetings', icon: <Video size={15} />, image: '/landing/meetings-panel', url: 'meet', disclosure: 'Shown with simulated participants.',
    title: 'Video calls under your own brand', body: 'Orkestra Meetings runs on your platform — no Zoom, no downloads for guests. Send a link, they join in the browser, and in-call chat keeps notes right in the room.' },
  { id: 'fleet', label: 'Fleet', icon: <Truck size={15} />, panel: <FleetPanel />, url: 'app/fleet',
    title: 'Keep the trucks on the road', body: 'Track vehicles and equipment with maintenance schedules by mileage or date. Orkestra sweeps for what’s due and flags it before a breakdown costs you a day.' },
]

export function ProductTourSection() {
  const [active, setActive] = useState(0)
  const t = TOUR[active]
  return (
    <section className="ork-section" id="platform">
      <div className="ork-container">
        <SectionHead
          eyebrow="The platform"
          title={<>See the work, not the workaround</>}
          lead="Six surfaces, one system, one source of truth. Tap through a real slice of Orkestra."
        />
        <div className="ork-tabs" role="tablist" aria-label="Product areas">
          {TOUR.map((tab, i) => (
            <button key={tab.id} role="tab" aria-selected={i === active} className="ork-tab" onClick={() => setActive(i)}>
              {tab.icon}{tab.label}
            </button>
          ))}
        </div>
        <div className="ork-split" style={{ gridTemplateColumns: '0.9fr 1.1fr' }}>
          <Reveal key={`copy-${t.id}`}>
            <div>
              <h3 className="ork-h3">{t.title}</h3>
              <p className="ork-lead" style={{ marginTop: 14, fontSize: 18 }}>{t.body}</p>
              <div style={{ marginTop: 22 }}>
                <Button to="/signup" variant="ghost">Explore in Orkestra <ArrowRight size={16} /></Button>
              </div>
            </div>
          </Reveal>
          <div className="ork-split__media">
            {t.image ? (
              // Self-chromed image — already a full window; do NOT double-frame.
              <div role="tabpanel" aria-label={t.label}>
                <picture>
                  <source srcSet={`${t.image}.webp`} type="image/webp" />
                  <img className="ork-tour-image" src={`${t.image}.png`} width={1180} height={619} loading="lazy" decoding="async"
                    alt="Orkestra Meetings — a browser-based video call showing four participant video tiles (one labelled Guest, one the active speaker), an in-call chat panel, and mic, camera, screen-share, and leave controls. Participants are simulated." />
                </picture>
                {t.disclosure && <p className="ork-caption" role="note">{t.disclosure}</p>}
              </div>
            ) : (
              <div className="ork-frame" role="tabpanel" aria-label={t.label}>
                <div className="ork-frame__bar">
                  <span className="ork-frame__dot" /><span className="ork-frame__dot" /><span className="ork-frame__dot" />
                  <span className="ork-frame__url">orkestra.app/{t.url}</span>
                </div>
                <div className="ork-frame__body" key={t.id}>{t.panel}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

// ═══ Capability breadth grid ═══════════════════════════════════════════════
const CAPS = [
  { icon: <Calendar size={20} />, t: 'Field dispatch', d: 'Calendar, board, and map scheduling with conflict detection, recurring jobs, and vendor assignment.' },
  { icon: <Users size={20} />, t: 'CRM', d: 'Contacts, companies, and a deal pipeline with tasks, activities, and forecasting.' },
  { icon: <FileText size={20} />, t: 'Quotes & invoicing', d: 'Line items, tax, discounts, recurring invoices, and captured job margin.' },
  { icon: <InboxIcon size={20} />, t: 'Shared inbox', d: 'Email, phone, and portal threads with assignment, priority, response-time SLA tracking, and search.' },
  { icon: <MessageSquare size={20} />, t: 'Team chat', d: 'Channels, DMs, threads, mentions, and reactions for the whole crew.' },
  { icon: <Video size={20} />, t: 'Orkestra Meetings', d: 'Own-brand guest video with in-call chat — no third-party account needed.' },
  { icon: <Truck size={20} />, t: 'Fleet management', d: 'Vehicles, equipment, maintenance schedules, odometer logs, and due sweeps.' },
  { icon: <ClipboardList size={20} />, t: 'Service requests', d: 'Multi-channel intake from portal, staff, or email — triaged to a job or resolution.' },
  { icon: <ShieldCheck size={20} />, t: 'Incident reporting', d: 'Evidence-grade, append-only logs with photos and an amendment audit trail.' },
  { icon: <Building2 size={20} />, t: 'Client & vendor portals', d: 'Customers track jobs and invoices; subcontractors accept work and post updates.' },
  { icon: <Calendar size={20} />, t: 'Sales calendar', d: 'Meetings with .ics invites and reminders, linked to deals and contacts.' },
  { icon: <BarChart3 size={20} />, t: 'Analytics', d: 'Revenue, margin, completion, and pipeline — live, not rebuilt by hand.' },
]
export function CapabilitiesSection() {
  return (
    <section className="ork-section" id="capabilities">
      <div className="ork-container">
        <SectionHead
          eyebrow="The whole operation"
          title={<>Everything it takes to run the business</>}
          lead="Not a CRM with add-ons. A single operational system where every part shares the same customers, jobs, and numbers."
        />
        <div className="ork-grid ork-grid--3">
          {CAPS.map((c, i) => (
            <Reveal key={c.t} delay={(i % 3) * 70}>
              <div className="ork-card ork-card--hover" style={{ height: '100%' }}>
                <div className="ork-card__icon">{c.icon}</div>
                <h3 className="ork-h3" style={{ fontSize: 17 }}>{c.t}</h3>
                <p className="ork-body" style={{ marginTop: 7, fontSize: 14.5 }}>{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

// ═══ Analytics ═════════════════════════════════════════════════════════════
export function AnalyticsSection() {
  return (
    <section className="ork-section" id="analytics">
      <div className="ork-container">
        <div className="ork-split ork-split--rev">
          <Reveal>
            <div>
              <span className="ork-eyebrow ork-eyebrow--pill">Analytics</span>
              <h2 className="ork-h2" style={{ marginTop: 14 }}>The numbers add up, <span className="ork-gradient-text">automatically</span>.</h2>
              <p className="ork-lead" style={{ marginTop: 16 }}>
                Because the job, the quote, and the invoice all live in one system, your dashboards are always current. Revenue, margin, completion rate, and deal pipeline update the moment work happens — no exports, no spreadsheets, no month-end scramble.
              </p>
              <ul className="ork-feature-list">
                {['Live revenue and margin, per job and in aggregate', 'Deal pipeline with win-rate and forecast', 'Completion and hold-rate trends over any window'].map(x => (
                  <li key={x}><Check size={17} />{x}</li>
                ))}
              </ul>
            </div>
          </Reveal>
          <div className="ork-split__media">
            <Reveal>
              <div className="ork-frame">
                <div className="ork-frame__bar"><span className="ork-frame__dot" /><span className="ork-frame__dot" /><span className="ork-frame__dot" /><span className="ork-frame__url">orkestra.app/app/analytics</span></div>
                <div className="ork-frame__body"><AnalyticsPanel /></div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  )
}

// ═══ AI & Automations (dark stage) ═════════════════════════════════════════
export function AISection() {
  const outcomes = [
    { icon: <Repeat size={18} />, t: 'Runs the busywork', d: 'Recurring invoices generate and send, payment reminders go out, and maintenance-due checks sweep the fleet — on schedule, without you.' },
    { icon: <Bot size={18} />, t: 'Drafts, you approve', d: 'AI writes the reply, summarizes the thread, and preps the follow-up. A person always approves before anything reaches a customer.' },
    { icon: <Radio size={18} />, t: 'Shows its work', d: 'A live Automation Health view: heartbeat, next-run countdowns, and every processor run — so you can trust the automation because you can see it.' },
  ]
  return (
    <div data-theme="dark" className="ork-stage">
      <section className="ork-section" id="ai">
        <div className="ork-container">
          <SectionHead
            eyebrow="AI & Automations"
            title={<>Automation you can <span className="ork-gradient-text">actually see</span></>}
            lead="Orkestra takes the repetitive work off your plate — and unlike a black box, it shows you exactly what it did. AI assists; humans stay in control."
          />
          <div className="ork-split">
            <div>
              {outcomes.map((o, i) => (
                <Reveal key={o.t} delay={i * 80}>
                  <div style={{ display: 'flex', gap: 16, padding: '18px 0', borderTop: i ? '1px solid var(--border-subtle)' : 'none' }}>
                    <span style={{ width: 42, height: 42, borderRadius: 11, flexShrink: 0, background: 'var(--accent-dim)', color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{o.icon}</span>
                    <div>
                      <h3 className="ork-h3" style={{ fontSize: 18 }}>{o.t}</h3>
                      <p className="ork-body" style={{ marginTop: 6 }}>{o.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <div className="ork-split__media">
              <Reveal>
                <div className="ork-frame">
                  <div className="ork-frame__bar"><span className="ork-frame__dot" /><span className="ork-frame__dot" /><span className="ork-frame__dot" /><span className="ork-frame__url">orkestra.app/app/automation-health</span></div>
                  <div className="ork-frame__body"><AutomationPanel /></div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

// ═══ Roles — one platform, every seat ══════════════════════════════════════
const ROLES = [
  { who: 'Owners', icon: <Building2 size={18} />, get: 'Live margin, pipeline, and one place the whole operation reports up to.' },
  { who: 'Dispatchers', icon: <Calendar size={18} />, get: 'A board that shows every tech and flags conflicts before they cost a callback.' },
  { who: 'Office & admin', icon: <FileText size={18} />, get: 'Quotes, invoices, and a shared inbox where nothing slips through.' },
  { who: 'Field techs', icon: <Wrench size={18} />, get: 'Their day, the site details, and status updates from wherever they are.' },
  { who: 'Customers', icon: <Users size={18} />, get: 'A portal to see jobs, quotes, and invoices — and request more work.' },
  { who: 'Subcontractors', icon: <HardHat size={18} />, get: 'Accept assignments, post updates, and log job photos in a vendor portal.' },
]
export function RolesSection() {
  return (
    <section className="ork-section">
      <div className="ork-container">
        <SectionHead eyebrow="Every seat" title={<>One platform, the whole crew</>} lead="From the owner’s dashboard to the tech’s next stop to the customer’s portal — everyone works from the same source of truth." />
        <div className="ork-grid ork-grid--3">
          {ROLES.map((r, i) => (
            <Reveal key={r.who} delay={(i % 3) * 70}>
              <div className="ork-card" style={{ height: '100%', display: 'flex', gap: 14 }}>
                <span style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0, background: 'var(--accent-dim)', color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{r.icon}</span>
                <div>
                  <h3 className="ork-h3" style={{ fontSize: 16.5 }}>{r.who}</h3>
                  <p className="ork-body" style={{ marginTop: 5, fontSize: 14 }}>{r.get}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

// ═══ Device / responsive montage ═══════════════════════════════════════════
export function DeviceSection() {
  return (
    <section className="ork-section">
      <div className="ork-container">
        <SectionHead eyebrow="Anywhere the work is" title={<>Command center at the desk. Job list in the truck.</>} lead="Orkestra is built for the office and the field — the same live data on desktop, tablet, and the phone in a tech’s pocket." />
        <Reveal>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 'clamp(-40px, -4vw, 0px)', flexWrap: 'wrap' }}>
            <div className="ork-frame" style={{ flex: '1 1 420px', maxWidth: 620 }}>
              <div className="ork-frame__bar"><span className="ork-frame__dot" /><span className="ork-frame__dot" /><span className="ork-frame__dot" /><span className="ork-frame__url">orkestra.app/app/field</span></div>
              <div className="ork-frame__body"><DispatchBoard compact /></div>
            </div>
            <div data-theme="dark" style={{ width: 208, borderRadius: 30, padding: 9, background: 'var(--surface-raised)', border: '1px solid var(--border-strong)', boxShadow: 'var(--popup-shadow)', flexShrink: 0 }}>
              <div style={{ borderRadius: 22, overflow: 'hidden', background: 'var(--canvas)', border: '1px solid var(--border-subtle)' }}>
                <div style={{ height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ width: 54, height: 5, borderRadius: 999, background: 'var(--border-strong)' }} />
                </div>
                <MobileJobList />
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  )
}
function MobileJobList() {
  const items = [
    { t: '8:30 · Harborview Dental', s: 'No cooling — call', tone: 'var(--danger)' },
    { t: '9:15 · Lakeside Grill', s: 'Compressor swap', tone: 'var(--status-job-awaiting-approval)' },
    { t: '10:00 · Cedar Ridge HOA', s: 'Belt & filter', tone: 'var(--status-job-scheduled)' },
    { t: '1:00 · Sunrise Senior', s: 'Pump inspection', tone: 'var(--status-job-pending-parts)' },
  ]
  return (
    <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 7 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--ink-primary)', padding: '2px 4px 6px' }}>My day · Priya</div>
      {items.map(it => (
        <div key={it.t} style={{ padding: '9px 10px', borderRadius: 10, background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)', borderLeft: `3px solid ${it.tone}` }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--ink-primary)' }}>{it.t}</div>
          <div style={{ fontSize: 10.5, color: 'var(--ink-tertiary)' }}>{it.s}</div>
        </div>
      ))}
    </div>
  )
}

// ═══ Before / after comparison ═════════════════════════════════════════════
const COMPARE_ROWS = [
  'Customer, job, and invoice in one record',
  'Schedule conflicts caught automatically',
  'Real job margin, not a month-end guess',
  'Shared inbox + team chat in the same place',
  'Video meetings under your own brand',
  'Fleet maintenance tracked and swept',
  'Dashboards that update themselves',
]
export function CompareSection() {
  return (
    <section className="ork-section">
      <div className="ork-container ork-container--tight">
        <SectionHead eyebrow="Before & after" title={<>The disconnected stack vs. Orkestra</>} />
        <Reveal>
          <div className="ork-compare">
            <div className="ork-compare__cell ork-compare__cell--head ork-compare__row-label">&nbsp;</div>
            <div className="ork-compare__cell ork-compare__cell--head ork-compare__cell--muted" style={{ justifyContent: 'center' }}>Duct-taped tools</div>
            <div className="ork-compare__cell ork-compare__cell--head ork-compare__col--ork" style={{ justifyContent: 'center', color: 'var(--accent-text)' }}>Orkestra</div>
            {COMPARE_ROWS.map(r => (
              <React.Fragment key={r}>
                <div className="ork-compare__cell ork-compare__row-label">{r}</div>
                <div className="ork-compare__cell" style={{ justifyContent: 'center' }}><X size={18} style={{ color: 'var(--ink-tertiary)' }} /></div>
                <div className="ork-compare__cell ork-compare__col--ork" style={{ justifyContent: 'center' }}><Check size={18} style={{ color: 'var(--positive)' }} /></div>
              </React.Fragment>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  )
}

// ═══ Security / trust ══════════════════════════════════════════════════════
export function SecuritySection() {
  const points = [
    { icon: <ShieldCheck size={20} />, t: 'Your data stays yours', d: 'Every workspace is isolated at the database layer. Video runs under your own brand — meetings and messages live in your platform, not a third party’s.' },
    { icon: <ClipboardList size={20} />, t: 'An audit trail by design', d: 'Incident logs are append-only and evidence-grade, with amendment history. Sensitive records are corrected, never quietly erased.' },
    { icon: <Radio size={20} />, t: 'Observability built in', d: 'Automation Health shows a live heartbeat and every scheduled run, so the work happening in the background is never a mystery.' },
  ]
  return (
    <section className="ork-section">
      <div className="ork-container">
        <SectionHead eyebrow="Trust" title={<>Built for the record</>} lead="Service businesses carry liability, warranties, and customer trust. Orkestra treats your operational data like it matters." />
        <div className="ork-grid ork-grid--3">
          {points.map((p, i) => (
            <Reveal key={p.t} delay={i * 80}>
              <div className="ork-card" style={{ height: '100%' }}>
                <div className="ork-card__icon" style={{ background: 'var(--positive-dim)', color: 'var(--positive)' }}>{p.icon}</div>
                <h3 className="ork-h3" style={{ fontSize: 17 }}>{p.t}</h3>
                <p className="ork-body" style={{ marginTop: 7, fontSize: 14.5 }}>{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

// ═══ FAQ ═══════════════════════════════════════════════════════════════════
const FAQS = [
  { q: 'What kind of business is Orkestra for?', a: 'Service businesses that run field work and have outgrown a pile of separate tools — HVAC, plumbing, electrical, cleaning, landscaping, facilities, and similar trades running crews and, often, subcontractors.' },
  { q: 'What does Orkestra replace?', a: 'For most teams: a CRM, a field-scheduling app, a quoting/invoicing tool, a shared inbox, a team chat app, and a video-meeting tool — plus the spreadsheets holding it all together. It’s one system with one set of customers, jobs, and numbers.' },
  { q: 'Do my customers and subcontractors need accounts?', a: 'Customers get a lightweight portal to view jobs, quotes, and invoices and to send requests. Subcontractors get a vendor portal to accept work and post updates. Meeting guests just click a link — no account, no download.' },
  { q: 'Is the AI going to email my customers on its own?', a: 'No. AI drafts replies, summarizes threads, and preps follow-ups, but a person always approves before anything goes out. Automations like recurring invoices and reminders run on schedules you set — and you can watch them run in Automation Health.' },
  { q: 'How do I get started?', a: 'Create a workspace and you’re in — set up your team and start scheduling the same day. No sales call required to try it.' },
]
export function FAQSection() {
  const [open, setOpen] = useState<number | null>(0)
  return (
    <section className="ork-section" id="faq">
      <div className="ork-container">
        <SectionHead eyebrow="Questions" title="Good questions, straight answers" />
        <div className="ork-faq">
          {FAQS.map((f, i) => (
            <div className="ork-faq__item" key={f.q} data-open={open === i}>
              <button className="ork-faq__q" aria-expanded={open === i} onClick={() => setOpen(open === i ? null : i)}>
                {f.q}<Plus className="ork-faq__icon" size={20} />
              </button>
              {open === i && <div className="ork-faq__a">{f.a}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// Small proof strip of teammates (used in hero) — exported for reuse.
export function TeamStack() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ display: 'flex' }}>
        {TEAM.slice(0, 5).map((p, i) => (
          <span key={p.id} style={{ marginLeft: i ? -8 : 0 }}><Avatar initials={p.initials} grad={p.grad} size={30} ring /></span>
        ))}
      </div>
      <span style={{ fontSize: 13.5, color: 'var(--ink-tertiary)' }}>Built for the whole team — office to field.</span>
    </div>
  )
}
